import type { ReactNode } from 'react'
import { Paper } from '@mantine/core'
import { IconAlertTriangle, IconArrowRight, IconTrendingDown } from '@tabler/icons-react'
import { ChangeText } from '../components/ChangeText'
import { ClosureTimeChart } from '../components/ClosureTimeChart'
import { InfoIcon } from '../components/InfoIcon'
import { SectionCard } from '../components/SectionCard'
import { formatCount, formatDayCount, formatDays, formatSlice } from '../format'
import {
  usePopulationTrend,
  type AgingBucket,
  type CohortTarget,
  type PopulationQuery,
  type TrendScope,
  type usePopulationMeasures,
} from '../hooks'
import { measuresInScope, supportingMeasure } from '../utils'

const bucketLabel = ({ minDays, maxDays }: AgingBucket) =>
  maxDays === null ? `> ${minDays - 1} days` : `${minDays}–${maxDays} days`

function AgingPanel({
  buckets,
  subject,
  definition,
}: {
  buckets: AgingBucket[]
  subject: string
  definition: string
}) {
  const total = buckets.reduce((sum, b) => sum + b.count, 0)
  const share = (count: number) => (total > 0 ? Math.round((count / total) * 100) : 0)
  return (
    <Paper withBorder radius="lg" p="lg">
      <div className="mb-4 flex items-center gap-1 text-sm font-semibold text-slate-900">
        Open care gap aging · {subject}
        <InfoIcon label="Open care gap aging" definition={definition} />
      </div>
      {total === 0 ? (
        <p className="text-sm text-slate-500">No open care gaps at the end of the period.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {buckets.map((b) => (
            <li
              key={b.minDays}
              className="grid grid-cols-[5.5rem_1fr_5.5rem] items-center gap-3 text-sm"
            >
              <span className="text-slate-700">{bucketLabel(b)}</span>
              <span className="relative h-3 rounded-full bg-slate-100">
                <span
                  className="absolute inset-y-0 left-0 rounded-full bg-blue-400"
                  style={{ width: `${share(b.count)}%` }}
                />
              </span>
              <span className="text-right text-slate-700 tabular-nums">
                {formatCount(b.count)} ({share(b.count)}%)
              </span>
            </li>
          ))}
        </ul>
      )}
    </Paper>
  )
}

/**
 * The trend row under the family cards, for the selected scope (all families, one family group
 * or one measure): median time to resolve per slice with the latest slice against the comparison
 * period's last slice, open-gap aging at the period's end, and a callout on the measure whose
 * time to resolve changed most.
 */
export function ClosureTrend({
  query,
  scope,
  subject,
  measures,
  onOpenCohort,
  scopeControl,
}: {
  query: PopulationQuery
  scope: TrendScope
  /** The scope's label: "All families", a family group or a measure. */
  subject: string
  measures: ReturnType<typeof usePopulationMeasures>
  onOpenCohort: (target: CohortTarget) => void
  /** The scope selector, on the chart's title line. */
  scopeControl?: ReactNode
}) {
  const trend = usePopulationTrend(query, scope)
  const data = trend.data
  // The callout follows the scope, so it never points at an unrelated cohort, and waits for
  // the current selection's measures: the previous ones would open a cohort under the new
  // filters that their figures don't describe.
  const insight = measures.isPlaceholderData
    ? undefined
    : supportingMeasure(measuresInScope(measures.data?.measures ?? [], scope))
  const faster = (insight?.closureTime.changeDays ?? 0) < 0

  return (
    <section aria-label="Resolution trend and aging" className="flex flex-col gap-4">
      {/* Without a chart (first load, or this scope failed) the selector sits above the card,
          outside its error state, so another scope can still be chosen. */}
      {!data && scopeControl && <div className="flex justify-end">{scopeControl}</div>}
      <SectionCard title="Median time to resolve trend" query={trend} frame="open" hideTitle>
        {data && (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <Paper withBorder radius="lg" p="lg">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-1 text-sm font-semibold text-slate-900">
                  Median time to resolve trend · {subject}
                  <InfoIcon
                    label="Median time to resolve trend"
                    definition={data.definitions.trend}
                  />
                </div>
                {scopeControl}
              </div>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                <div className="min-w-0 flex-1">
                  <ClosureTimeChart points={data.points} subject={subject} />
                </div>
                {/* Always present here: only the sparklines ask for points only. */}
                {data.latest && (
                  <div className="flex shrink-0 flex-col gap-1 lg:w-48">
                    <span className="text-3xl font-semibold text-blue-700 tabular-nums">
                      {formatDays(data.latest.current.medianDays)}
                    </span>
                    <span className="text-sm text-slate-500">
                      Latest: {formatSlice(data.latest.current)}
                    </span>
                    <ChangeText
                      value={data.latest.changeDays}
                      unit="days"
                      better="down"
                      label={`vs ${formatSlice(data.latest.comparison)}`}
                    />
                  </div>
                )}
              </div>
            </Paper>
            <AgingPanel
              buckets={data.aging}
              subject={subject}
              definition={data.definitions.aging}
            />
          </div>
        )}
      </SectionCard>

      {insight && (
        <div
          className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 text-sm ${
            faster ? 'border-emerald-200 bg-emerald-50/70' : 'border-amber-200 bg-amber-50/70'
          }`}
        >
          <div className="flex min-w-0 items-start gap-2">
            {faster ? (
              <IconTrendingDown
                size={18}
                className="mt-0.5 shrink-0 text-emerald-600"
                aria-hidden
              />
            ) : (
              <IconAlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" aria-hidden />
            )}
            <div className="min-w-0">
              <div className="font-semibold text-slate-900">
                {faster ? 'Biggest improvement' : 'Biggest decline'}
              </div>
              <div className="text-slate-700">
                <span className="font-medium">{insight.label}</span>: median time to resolve{' '}
                {formatDays(insight.closureTime.current.medianDays)}, {faster ? 'down' : 'up'}{' '}
                {formatDayCount(Math.abs(insight.closureTime.changeDays!))} from{' '}
                {formatDays(insight.closureTime.comparison.medianDays)}.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenCohort({ type: 'measure', slug: insight.slug })}
            className={`inline-flex shrink-0 cursor-pointer items-center gap-1 text-sm font-semibold hover:underline ${
              faster ? 'text-emerald-700' : 'text-amber-700'
            }`}
          >
            Open supporting patients
            <IconArrowRight size={14} aria-hidden />
          </button>
        </div>
      )}
    </section>
  )
}
